import { z } from 'zod';
import { canonicalToolToProvider } from './toolNameTransport.js';
const limitSchema = z.object({
    limit: z.coerce.number().int().min(1).max(25).optional(),
});
const emptySchema = z.object({}).strict();
export const COPILOT_TOOL_DEFINITIONS = [
    {
        name: 'platform.getCapability',
        description: 'Haal productstatus (live/partial/planned/deprecated/unknown) en projectbeschikbaarheid op voor één capability-id.',
        permission: 'read',
        requiredCapabilities: ['automations_read'],
        inputSchema: z.object({ capability_id: z.string().min(1).max(120) }).strict(),
        auditPolicy: {
            inputSummaryFields: ['capability_id'],
            summarizeResult: (data) => {
                const d = data;
                return {
                    id: d.id ?? d.capability_id,
                    productStatus: d.productStatus,
                    available: d.available,
                    reason: d.reason,
                };
            },
        },
        parametersJsonSchema: {
            type: 'object',
            properties: { capability_id: { type: 'string' } },
            required: ['capability_id'],
            additionalProperties: false,
        },
    },
    {
        name: 'platform.searchDocumentation',
        description: 'Zoek in gecontroleerde FO/ADR/productdocumentatie (lexical RAG).',
        permission: 'read',
        requiredCapabilities: ['automations_read'],
        inputSchema: z.object({ query: z.string().min(2).max(500) }).strict(),
        auditPolicy: {
            inputSummaryFields: ['query'],
            summarizeResult: (data) => {
                const d = data;
                return { hit_count: Array.isArray(d.hits) ? d.hits.length : 0 };
            },
        },
        parametersJsonSchema: {
            type: 'object',
            properties: { query: { type: 'string' } },
            required: ['query'],
            additionalProperties: false,
        },
    },
    {
        name: 'project.getSummary',
        description: 'Read-only projectoverzicht: aantallen campagnes, doelgroepen, leads (geaggregeerd).',
        permission: 'read',
        requiredCapabilities: ['leads_read'],
        inputSchema: emptySchema,
        auditPolicy: {
            summarizeResult: (data) => {
                const d = data;
                return { counts: d.counts ?? null };
            },
        },
        parametersJsonSchema: { type: 'object', properties: {}, additionalProperties: false },
    },
    {
        name: 'project.getIntegrations',
        description: 'Aangesloten integraties met status en last_sync_at (geen secrets).',
        permission: 'read',
        requiredCapabilities: ['leads_read'],
        inputSchema: emptySchema,
        auditPolicy: {
            summarizeResult: (data) => {
                const d = data;
                const list = d.integrations ?? [];
                return {
                    integration_count: list.length,
                    connected_count: list.filter((i) => i.connected).length,
                };
            },
        },
        parametersJsonSchema: { type: 'object', properties: {}, additionalProperties: false },
    },
    {
        name: 'analytics.getGa4Summary',
        description: 'GA4 snapshot totalen + latest_snapshot_date + syncedAt voor dit project.',
        permission: 'read',
        requiredCapabilities: ['leads_read'],
        inputSchema: z.object({ days: z.coerce.number().int().min(1).max(90).optional() }).strict(),
        auditPolicy: {
            inputSummaryFields: ['days'],
            summarizeResult: (data) => {
                const d = data;
                return {
                    dataThrough: d.dataThrough,
                    syncedAt: d.syncedAt,
                    snapshot_days: d.snapshot_days,
                };
            },
        },
        parametersJsonSchema: {
            type: 'object',
            properties: { days: { type: 'integer', minimum: 1, maximum: 90, default: 28 } },
            additionalProperties: false,
        },
    },
    {
        name: 'analytics.getSearchConsoleSummary',
        description: 'GSC snapshot totalen + latest_snapshot_date + syncedAt voor dit project.',
        permission: 'read',
        requiredCapabilities: ['leads_read'],
        inputSchema: z.object({ days: z.coerce.number().int().min(1).max(90).optional() }).strict(),
        auditPolicy: {
            inputSummaryFields: ['days'],
            summarizeResult: (data) => {
                const d = data;
                return {
                    dataThrough: d.dataThrough,
                    syncedAt: d.syncedAt,
                    snapshot_days: d.snapshot_days,
                };
            },
        },
        parametersJsonSchema: {
            type: 'object',
            properties: { days: { type: 'integer', minimum: 1, maximum: 90, default: 28 } },
            additionalProperties: false,
        },
    },
    {
        name: 'campaign.list',
        description: 'Lijst campagnes in het actieve project (id, naam, status).',
        permission: 'read',
        requiredCapabilities: ['leads_read'],
        inputSchema: limitSchema.strict(),
        auditPolicy: {
            inputSummaryFields: ['limit'],
            summarizeResult: (data) => {
                const d = data;
                return { count: d.count ?? d.campaigns?.length ?? 0 };
            },
        },
        parametersJsonSchema: {
            type: 'object',
            properties: { limit: { type: 'integer', minimum: 1, maximum: 25, default: 15 } },
            additionalProperties: false,
        },
    },
    {
        name: 'campaign.getStats',
        description: 'Campagne KPI snapshot (enrollments, replies, mail sent/bounce).',
        permission: 'read',
        requiredCapabilities: ['leads_read'],
        inputSchema: z.object({ campaign_id: z.string().uuid() }).strict(),
        auditPolicy: {
            inputSummaryFields: ['campaign_id'],
            summarizeResult: (data) => {
                const d = data;
                const s = d.stats ?? {};
                return {
                    enrollments: s.enrolled ?? s.enrollments,
                    reply_rate: s.reply_rate,
                };
            },
        },
        parametersJsonSchema: {
            type: 'object',
            properties: { campaign_id: { type: 'string', description: 'UUID campagne' } },
            required: ['campaign_id'],
            additionalProperties: false,
        },
    },
    {
        name: 'audience.list',
        description: 'Doelgroepen in het project (id, naam, type).',
        permission: 'read',
        requiredCapabilities: ['leads_read'],
        inputSchema: limitSchema.strict(),
        auditPolicy: {
            inputSummaryFields: ['limit'],
            summarizeResult: (data) => {
                const d = data;
                return { count: d.count ?? d.audiences?.length ?? 0 };
            },
        },
        parametersJsonSchema: {
            type: 'object',
            properties: { limit: { type: 'integer', minimum: 1, maximum: 25, default: 15 } },
            additionalProperties: false,
        },
    },
    {
        name: 'audience.getKpi',
        description: 'Campagne-KPI rollup voor één doelgroep.',
        permission: 'read',
        requiredCapabilities: ['leads_read'],
        inputSchema: z.object({ audience_id: z.string().uuid() }).strict(),
        auditPolicy: {
            inputSummaryFields: ['audience_id'],
            summarizeResult: (data) => {
                const d = data;
                const k = d.kpi ?? {};
                return {
                    enrollments: k.enrollments,
                    reply_rate_pct: k.reply_rate_pct,
                };
            },
        },
        parametersJsonSchema: {
            type: 'object',
            properties: { audience_id: { type: 'string' } },
            required: ['audience_id'],
            additionalProperties: false,
        },
    },
];
export const COPILOT_TOOL_NAMES = COPILOT_TOOL_DEFINITIONS.map((t) => t.name);
const byName = new Map(COPILOT_TOOL_DEFINITIONS.map((t) => [t.name, t]));
export function getCopilotToolDefinition(name) {
    return byName.get(name);
}
export function copilotToolsToOpenAi(names) {
    return names
        .map((name) => byName.get(name))
        .filter((t) => Boolean(t))
        .map((t) => ({
        type: 'function',
        function: {
            name: canonicalToolToProvider(t.name),
            description: t.description,
            parameters: t.parametersJsonSchema,
        },
    }));
}
/** @deprecated Use COPILOT_TOOL_DEFINITIONS / copilotToolsToOpenAi */
export const COPILOT_READ_TOOL_NAMES = [
    'campaign.list',
    'campaign.getStats',
    'audience.list',
    'audience.getKpi',
    'analytics.getGa4Summary',
    'analytics.getSearchConsoleSummary',
];
/** @deprecated */
export const COPILOT_READ_TOOLS = copilotToolsToOpenAi([...COPILOT_READ_TOOL_NAMES]);
//# sourceMappingURL=copilotToolRegistry.js.map